/**
 * AI Config Page (US-16-GR-23)
 *
 * Reads and writes the admin AI configuration via adminConfigService,
 * bound to GET/PUT /api/admin/config/ai. Saving writes the server-side
 * Admin.ConfigUpdated Receipt.
 *
 * Fields (aligned to AiConfigResponse): confidenceThreshold (slider 0–1),
 * dailyEasyButtonQuota, the Basic/Pro/Premium per-tier daily quotas
 * (story 17921), and the Easy Button model.
 *
 * Story 17437: primaryProvider, fallbackProvider, wizardModel and
 * ghostWritingModel were removed from this page (and from the API contract
 * by the sibling API change). No AI request path read them — the wizard
 * model was additionally closed by DEC-2026-08-11 (SurveyWizard's price is
 * pinned and must change by decision, not by an admin knob), and the
 * provider fields are SKConfig routing (DEC-015 boundary) with no DB read
 * anywhere. AC1 leaves removal as the only option once "wire it up" is off
 * the table for all four.
 */

import { useCallback, useEffect, useRef, useState, startTransition, type ReactNode } from 'react';
import { RefreshCw, AlertCircle, CheckCircle, Cpu } from 'lucide-react';
import type { AIConfig, ModelCatalogRejection } from '../services/adminConfigService';
import { getAIConfig, updateAIConfig, ModelCatalogRejectionError } from '../services/adminConfigService';

// Curated FE-side model catalog, aligned to the backend's configured
// LLMProviders.AllowedModels (AI.API appsettings): model ids NOT in that
// allow-list are rejected server-side with a 400 (PUT /api/admin/config/ai),
// so this list must only offer ids the backend will actually honor. Gemini/
// Perplexity have no configured provider there today, hence no entries. The
// durable fix is a backend catalog endpoint (follow-up US). "Custom…" still
// lets an admin enter any model id manually — the server is the backstop.
const KNOWN_PROVIDERS = ['openai', 'anthropic', 'gemini', 'perplexity'] as const;

const MODELS_BY_PROVIDER: Record<(typeof KNOWN_PROVIDERS)[number], string[]> = {
  openai: ['gpt-4o-mini', 'gpt-4', 'gpt-4o', 'gpt-4.1'],
  anthropic: ['claude-sonnet-4-6', 'claude-haiku-4-5', 'claude-3-opus'],
  gemini: [],
  perplexity: [],
};

const ALL_MODEL_OPTIONS: { provider: (typeof KNOWN_PROVIDERS)[number]; model: string }[] =
  KNOWN_PROVIDERS.flatMap((provider) =>
    MODELS_BY_PROVIDER[provider].map((model) => ({ provider, model })),
  );

const TIER_QUOTAS = [
  { id: 'ai-basic-daily-quota', label: 'Basic Daily Quota', field: 'basicDailyQuota' },
  { id: 'ai-pro-daily-quota', label: 'Pro Daily Quota', field: 'proDailyQuota' },
  { id: 'ai-premium-daily-quota', label: 'Premium Daily Quota', field: 'premiumDailyQuota' },
] as const;

type TierField = (typeof TIER_QUOTAS)[number]['field'];
type TierText = Record<TierField, string>;

const tierTextFrom = (cfg: AIConfig): TierText => ({
  basicDailyQuota: String(cfg.basicDailyQuota),
  proDailyQuota: String(cfg.proDailyQuota),
  premiumDailyQuota: String(cfg.premiumDailyQuota),
});

const WHOLE_NUMBER = /^\d+$/;

const CUSTOM_MODEL_VALUE = '__custom__';

const PROVIDER_LABELS: Record<(typeof KNOWN_PROVIDERS)[number], string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Gemini',
  perplexity: 'Perplexity',
};

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block text-xs font-medium text-zinc-400 mb-1">
      {children}
    </label>
  );
}

function RejectionNote({ rejection }: { rejection?: ModelCatalogRejection }) {
  if (!rejection) return null;
  return (
    <p role="alert" className="text-xs text-red-400 mt-1">
      &lsquo;{rejection.value}&rsquo; is not in the allowed catalog. Allowed:{' '}
      {rejection.allowed.join(', ')}.
    </p>
  );
}

function ModelSelect({
  id,
  label,
  value,
  onChange,
  rejection,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  rejection?: ModelCatalogRejection;
}) {
  // isCustom/customValue are seeded once from the incoming value and then owned
  // locally. The parent remounts this component (via a `key` tied to load/save/
  // discard) whenever the underlying config is replaced wholesale, so there is
  // no need to resync these from props on every render — see AiConfigPage's
  // `resetKey`.
  const isKnown = ALL_MODEL_OPTIONS.some((opt) => opt.model === value);
  const [isCustom, setIsCustom] = useState(!isKnown && value !== '');
  const [customValue, setCustomValue] = useState(!isKnown ? value : '');

  const selectValue = isCustom ? CUSTOM_MODEL_VALUE : value;

  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <select
        id={id}
        value={selectValue}
        aria-label={label}
        onChange={(e) => {
          const next = e.target.value;
          if (next === CUSTOM_MODEL_VALUE) {
            setIsCustom(true);
            onChange(customValue);
          } else {
            setIsCustom(false);
            onChange(next);
          }
        }}
        className="w-64 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
      >
        <option value="" disabled>
          Select a model…
        </option>
        {KNOWN_PROVIDERS.filter((provider) => MODELS_BY_PROVIDER[provider].length > 0).map((provider) => (
          <optgroup key={provider} label={PROVIDER_LABELS[provider]}>
            {MODELS_BY_PROVIDER[provider].map((model) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </optgroup>
        ))}
        <option value={CUSTOM_MODEL_VALUE}>Custom…</option>
      </select>
      {isCustom && (
        <input
          id={`${id}-custom`}
          type="text"
          value={customValue}
          aria-label={`${label} custom value`}
          onChange={(e) => {
            setCustomValue(e.target.value);
            onChange(e.target.value);
          }}
          className="w-64 mt-2 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
          placeholder="e.g. gpt-4o"
        />
      )}
      <RejectionNote rejection={rejection} />
    </div>
  );
}

export function AiConfigPage() {
  const [config, setConfig] = useState<AIConfig | null>(null);
  const [draft, setDraft] = useState<AIConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // US-19-GR-81: captured so it can be cleared on unmount, instead of a bare setTimeout.
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);
  // Populated only by a 400 model-catalog rejection; keyed by field name so
  // each offending <select>/<input> can show its own note. Cleared at the
  // start of every save attempt so a stale rejection never lingers.
  const [rejections, setRejections] = useState<ModelCatalogRejection[]>([]);
  // Bumped whenever `draft` is wholesale replaced by a fresh config (load,
  // successful save, or discard) so the model <select>s below remount and
  // re-derive their local custom-mode state instead of needing an effect.
  const [resetKey, setResetKey] = useState(0);
  // Tier quota text is kept as typed so a cleared box is never silently saved as 0
  // (0 means unlimited). Reset wherever `draft` is wholesale replaced.
  const [tierText, setTierText] = useState<TierText | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cfg = await getAIConfig();
      setConfig(cfg);
      setDraft(cfg);
      setTierText(tierTextFrom(cfg));
      setResetKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    startTransition(() => {
      void load();
    });
  }, [load]);

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    setRejections([]);
    try {
      const updated = await updateAIConfig(draft);
      setConfig(updated);
      setDraft(updated);
      setTierText(tierTextFrom(updated));
      setResetKey((k) => k + 1);
      setSaved(true);
      savedTimerRef.current = setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      if (err instanceof ModelCatalogRejectionError) {
        setRejections(err.rejected);
      }
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const rejectionFor = (field: string): ModelCatalogRejection | undefined =>
    rejections.find((r) => r.field === field);

  const tierTextInvalid =
    !!tierText && TIER_QUOTAS.some(({ field }) => !WHOLE_NUMBER.test(tierText[field]));

  const isDirty = !!draft && !!config && JSON.stringify(draft) !== JSON.stringify(config);

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      <div className="flex items-center gap-2 mb-6">
        <Cpu className="w-5 h-5 text-violet-400" />
        <h1 className="text-lg font-semibold text-zinc-100">AI Configuration</h1>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-40">
          <RefreshCw className="w-5 h-5 text-zinc-500 animate-spin" />
        </div>
      )}

      {!loading && error && !draft && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg max-w-xl">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-400 font-medium">Failed to load AI configuration</p>
            <p className="text-xs text-zinc-500 mt-0.5">{error}</p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="ml-auto px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && draft && (
        <div className="space-y-6 max-w-xl">
          {/* Confidence threshold */}
          <div>
            <FieldLabel htmlFor="ai-confidence-threshold">
              Confidence Threshold —{' '}
              <span className="text-zinc-200 font-mono">{draft.confidenceThreshold.toFixed(2)}</span>
            </FieldLabel>
            <input
              id="ai-confidence-threshold"
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={draft.confidenceThreshold}
              onChange={(e) =>
                setDraft((d) => (d ? { ...d, confidenceThreshold: parseFloat(e.target.value) } : d))
              }
              className="w-full h-2 bg-zinc-700 rounded-full appearance-none cursor-pointer accent-violet-500"
            />
            <div className="flex justify-between text-xs text-zinc-600 mt-0.5">
              <span>0.00 (always ask)</span>
              <span>1.00 (always act)</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              AI acts autonomously when its confidence score exceeds this threshold.
            </p>
          </div>

          {/* Daily Easy Button quota */}
          <div>
            <FieldLabel htmlFor="ai-daily-quota">Daily Easy Button Quota (per user)</FieldLabel>
            <input
              id="ai-daily-quota"
              type="number"
              min={0}
              max={100}
              step={1}
              value={draft.dailyEasyButtonQuota}
              onChange={(e) =>
                setDraft((d) =>
                  d ? { ...d, dailyEasyButtonQuota: parseInt(e.target.value, 10) || 0 } : d,
                )
              }
              className="w-32 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Maximum Easy Button invocations per user per calendar day. 0 = unlimited.
            </p>
          </div>

          {/* Per-tier daily Easy Button quotas (story 17921) */}
          <div>
            <div className="flex flex-wrap gap-4">
              {TIER_QUOTAS.map(({ id, label, field }) => (
                <div key={id}>
                  <FieldLabel htmlFor={id}>{label}</FieldLabel>
                  <input
                    id={id}
                    type="number"
                    min={0}
                    step={1}
                    value={tierText?.[field] ?? String(draft[field])}
                    aria-invalid={tierText ? !WHOLE_NUMBER.test(tierText[field]) : undefined}
                    onChange={(e) => {
                      const text = e.target.value;
                      setTierText((t) => (t ? { ...t, [field]: text } : t));
                      if (WHOLE_NUMBER.test(text)) {
                        setDraft((d) => (d ? { ...d, [field]: parseInt(text, 10) } : d));
                      }
                    }}
                    className="w-32 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Easy Button uses per user per day for each paid tier. 0 means no daily limit.
            </p>
            {tierTextInvalid && (
              <p role="alert" className="text-xs text-red-400 mt-1">
                Enter a whole number for each tier (0 means no daily limit).
              </p>
            )}
          </div>

          {/* Easy Button model */}
          <div className="space-y-4">
            <p className="text-xs font-medium text-zinc-400">Models by Use Case</p>
            <ModelSelect
              key={`ai-easybutton-model-${resetKey}`}
              id="ai-easybutton-model"
              label="Easy Button Model"
              value={draft.easyButtonModel}
              onChange={(v) => setDraft((d) => (d ? { ...d, easyButtonModel: v } : d))}
              rejection={rejectionFor('easyButtonModel')}
            />
          </div>

          {/* Save / feedback */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || !isDirty || tierTextInvalid}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-zinc-950 text-sm font-medium hover:bg-violet-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
            >
              {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>

            {saved && (
              <span className="flex items-center gap-1.5 text-sm text-emerald-400">
                <CheckCircle className="w-4 h-4" />
                Saved
              </span>
            )}

            {error && draft && (
              <span className="flex items-center gap-1.5 text-sm text-red-400">
                <AlertCircle className="w-4 h-4" />
                {error}
              </span>
            )}

            {isDirty && (
              <button
                type="button"
                onClick={() => {
                  setDraft(config);
                  if (config) setTierText(tierTextFrom(config));
                  setResetKey((k) => k + 1);
                }}
                className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                Discard changes
              </button>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

export default AiConfigPage;
